import { AdminTitle } from "@/admin/AdminTitle.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import G from "@/components/Grid.module.css";
import { Separator } from "@/components/Separator.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import FF from "@/components/forms/FormField.module.css";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { siteCurrencyInfoAtom } from "@/modules/currency/currencyInfo.ts";
import { currencyFormats } from "@/modules/pricing/currencies.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import type { DisplayCurrency } from "@warrenio/api-spec/spec.oats.gen";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import invariant from "tiny-invariant";

export function CurrenciesView() {
    const api = useApiClient();
    const siteCurrencyInfo = useAtomValue(siteCurrencyInfoAtom);
    const [items, setItems] = useState<DisplayCurrency[]>([]);
    const path = "/ui/currencies";
    const existingCodes = items.map((item) => item.code);

    const defaultCurrency = useMemo(
        () => ({
            code: siteCurrencyInfo.code,
            title: siteCurrencyInfo.code,
            rate: 1,
        }),
        [siteCurrencyInfo.code],
    );

    const getValue = useCallback(async () => {
        const { data } = await api.GET("/configuration/admin/value", {
            params: { query: { path } },
        });

        invariant(typeof data === "string" || data == null, "Expected string value");

        const dataJson = data ? JSON.parse(data) : [];
        const dataItems: DisplayCurrency[] =
            Array.isArray(dataJson) && dataJson.length ? (dataJson as DisplayCurrency[]) : [defaultCurrency];

        if (!dataItems.some((item) => item.code === defaultCurrency.code)) {
            dataItems.unshift(defaultCurrency);
        }

        setItems(dataItems);
    }, [api, path, defaultCurrency]);

    // initial data loading
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- The async fetch populates local component state.
        discardPromise(getValue());
    }, [getValue]);

    const allCurrencies = Object.entries(currencyFormats).map(([code, data]) => ({
        ...data,
        code,
    }));

    function setCode(value: string, index: number) {
        const newItems = [...items];
        newItems[index].code = value;
        newItems[index].title = value;
        setItems(newItems);
    }

    function setRate(value: number, index: number) {
        const newItems = [...items];
        newItems[index].rate = value;
        setItems(newItems);
    }

    function addRow() {
        const defaultCode = allCurrencies.find((currency) => !existingCodes.includes(currency.code));
        const newItem = {
            code: defaultCode?.code ?? "",
            title: defaultCode?.code ?? "",
            rate: 1,
        };

        const newItems = [...items, newItem];

        setItems(newItems);
    }

    function deleteRow(index: number) {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    }

    async function onSubmit() {
        const newErrors = items.map((item) => ({
            code: !item.code,
            rate: Number.isNaN(item.rate) || item.rate <= 0,
        }));

        const hasErrors = newErrors.some((err) => err.code || err.rate);

        if (hasErrors) {
            return;
        }

        const data = items.sort((a, b) => {
            if (a.code === siteCurrencyInfo.code) return -1;
            if (b.code === siteCurrencyInfo.code) return 1;
            return a.code.localeCompare(b.code);
        });

        await api.POST("/configuration/admin/value", {
            body: { path, data: JSON.stringify(data) },
            ...jsonEncodedBody,
        });
        await getValue();
    }

    return (
        <>
            <AdminTitle title="Currencies" />

            <div className="p-2">
                <p className="color-muted">
                    Select display currency you want to show on the platform for end-users. Displayed amounts and
                    exchange rates are indicative and for informational purposes only. All transactions are processed in
                    the underlying currency (or in the currency defined by payment method), and final charges may vary
                    based on your payment provider's rates.
                </p>
            </div>

            <Separator />

            <ContentPane>
                <div className={G.WarrenGrid}>
                    <div>
                        <WTable>
                            <thead>
                                <tr>
                                    <th className="w-11rem">Currency</th>
                                    <th className="w-12rem">Exchange rate</th>
                                    <th />
                                    <th />
                                </tr>
                            </thead>
                            <WTableBody>
                                {items.map((currency, index) => (
                                    <tr key={index}>
                                        <td>
                                            <WSelect
                                                disabledKeys={existingCodes}
                                                isRequired
                                                isDisabled={currency.code === siteCurrencyInfo.code}
                                                getKey={(item) => item.code}
                                                searchItems
                                                items={allCurrencies}
                                                aria-label="Select currency"
                                                getTextValue={(item) => item.code}
                                                valueKey={currency.code}
                                                onChange={(item) => setCode(item.code, index)}
                                            >
                                                {({ code }) => code}
                                            </WSelect>
                                        </td>
                                        <td>
                                            <WNumberField
                                                isRequired
                                                isReadOnly={currency.code === siteCurrencyInfo.code}
                                                headless
                                                wide
                                                aria-label="Rate"
                                                onChange={(value) => setRate(value, index)}
                                                value={currency.rate}
                                                formatOptions={{
                                                    maximumFractionDigits: 10,
                                                }}
                                            >
                                                <AInput />
                                            </WNumberField>
                                        </td>
                                        <td>= 1 {siteCurrencyInfo.code}</td>
                                        <td className="text-right">
                                            <WTooltip text="Delete">
                                                <WButton
                                                    isDisabled={currency.code === siteCurrencyInfo.code}
                                                    ariaLabel="Delete"
                                                    color="primary"
                                                    variant="ghost"
                                                    size="xs"
                                                    icon="jp-trash-icon"
                                                    action={() => deleteRow(index)}
                                                />
                                            </WTooltip>
                                        </td>
                                    </tr>
                                ))}
                            </WTableBody>
                        </WTable>

                        <div className="pt-3">
                            <WButton
                                className="w-full"
                                size="lg"
                                color="primary"
                                variant="dashed"
                                icon="jp-icon-add"
                                action={addRow}
                            >
                                Add new currency
                            </WButton>
                        </div>
                    </div>
                </div>
            </ContentPane>

            <Separator />

            <div className={FF.FormActions}>
                <WButton color="primary" variant="basic" size="md" action={async () => await onSubmit()}>
                    Save
                </WButton>
            </div>
        </>
    );
}
